'use client';

import { FC } from 'react';
import { isFilled } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText } from '@prismicio/react';
import { MotionSafeVideo } from '@/components/common/MotionSafeVideo';
import { useIsMobile } from '@/hooks/useIsMobile';

type PhaseCard = {
  phase_name?: string;
  cta_label?: string;
  cta_link?: import('@prismicio/client').LinkField;
  card_background?: import('@prismicio/client').ImageField;
  deliverables_text?: string;
  capabilities_text?: string;
};

type DefaultPrimary = {
  tagline?: string;
  hero_image?: import('@prismicio/client').ImageField;
  title?: import('@prismicio/client').RichTextField;
  row_1_label?: string;
  row_2_label?: string;
  phase_cards?: PhaseCard[];
  footer_note?: string;
};

type WithVideoPrimary = {
  tagline?: string;
  hero_video?: import('@prismicio/client').LinkToMediaField;
  hero_poster?: import('@prismicio/client').ImageField;
  title?: import('@prismicio/client').RichTextField;
  table_background?: import('@prismicio/client').ImageField;
  row_1_label?: string;
  row_2_label?: string;
  phase_cards?: PhaseCard[];
  footer_note?: string;
};

type VelxPhaseComparisonSlice =
  | {
      slice_type: string;
      variation: 'default';
      primary: DefaultPrimary;
    }
  | {
      slice_type: string;
      variation: 'withVideo';
      primary: WithVideoPrimary;
    }
  | {
      slice_type: string;
      variation: string;
      primary: DefaultPrimary & WithVideoPrimary;
    };

const GREEN = '#00F557';
const HEADER_H = 'min-h-[140px]';
const ROW_H = 'min-h-[140px]';

const PhaseCtaButton: FC<{
  cta_label?: string;
  cta_link?: import('@prismicio/client').LinkField;
  variant?: 'compact' | 'full';
}> = ({ cta_label, cta_link, variant = 'compact' }) => {
  if (!cta_label && !isFilled.link(cta_link)) {
    return null;
  }
  const baseCls =
    variant === 'full'
      ? 'inline-flex items-center justify-center rounded-full border border-white bg-transparent px-5 py-2 font-manrope text-[13px] font-semibold leading-[1.4] text-white transition-colors hover:bg-white hover:text-[#041118]'
      : 'relative inline-flex items-center justify-center rounded-full border border-white/80 bg-transparent px-5 py-2 font-manrope text-[13px] font-semibold leading-[1.4] text-white transition-colors hover:bg-white hover:text-[#041118]';
  return isFilled.link(cta_link) ? (
    <PrismicNextLink field={cta_link} className={baseCls}>
      {cta_label || 'View phase'}
    </PrismicNextLink>
  ) : (
    <span className={baseCls}>{cta_label}</span>
  );
};

const PhaseHeader: FC<{ card: PhaseCard }> = ({ card }) => (
  <div className="relative flex min-h-[140px] flex-col items-start gap-4 overflow-hidden rounded-[16px] bg-[#0A1620] px-5 pb-6 pt-5">
    {isFilled.image(card.card_background) && (
      <div className="pointer-events-none absolute inset-0">
        <PrismicNextImage
          field={card.card_background}
          fill
          className="object-cover opacity-60"
          sizes="320px"
          fallbackAlt=""
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A1620]" />
      </div>
    )}
    {card.phase_name && (
      <p
        className="relative m-0 text-[18px] font-semibold leading-[1.2]"
        style={{ color: GREEN }}
      >
        {card.phase_name}
      </p>
    )}
    <PhaseCtaButton cta_label={card.cta_label} cta_link={card.cta_link} />
  </div>
);

const PhaseFullCard: FC<{ card: PhaseCard }> = ({ card }) => (
  <div className="relative flex flex-col overflow-hidden rounded-[20px] border border-white/10">
    {isFilled.image(card.card_background) && (
      <div className="pointer-events-none absolute inset-0">
        <PrismicNextImage
          field={card.card_background}
          fill
          className="object-cover"
          sizes="(min-width: 1024px) 360px, 100vw"
          fallbackAlt=""
        />
        <div className="absolute inset-0 bg-[#041118]/55" />
      </div>
    )}

    <div
      className={`relative flex ${HEADER_H} flex-col items-start gap-4 px-6 pb-6 pt-6`}
    >
      {card.phase_name && (
        <p
          className="m-0 text-[20px] font-semibold leading-[1.2]"
          style={{ color: GREEN }}
        >
          {card.phase_name}
        </p>
      )}
      <PhaseCtaButton
        cta_label={card.cta_label}
        cta_link={card.cta_link}
        variant="full"
      />
    </div>

    <div
      className={`relative ${ROW_H} border-t border-white/10 px-6 py-6 flex items-start`}
    >
      {card.deliverables_text && (
        <p className="m-0 text-[14px] font-normal leading-[1.5] text-white">
          {card.deliverables_text}
        </p>
      )}
    </div>

    <div
      className={`relative ${ROW_H} border-t border-white/10 px-6 py-6 flex items-start`}
    >
      {card.capabilities_text && (
        <p className="m-0 text-[14px] font-normal leading-[1.5] text-white">
          {card.capabilities_text}
        </p>
      )}
    </div>
  </div>
);

const VelxPhaseComparison: FC<{ slice: VelxPhaseComparisonSlice }> = ({
  slice,
}) => {
  const isWithVideo = slice.variation === 'withVideo';
  const isMobile = useIsMobile({ width: 1023 });
  const { tagline, title, row_1_label, row_2_label, phase_cards, footer_note } =
    slice.primary;

  const cards = (phase_cards ?? []).slice(0, 3);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative overflow-hidden bg-[#041118] font-inter"
    >
      <div className="mx-auto w-full max-w-[1200px] px-4 py-16 md:px-10 md:py-20">
        {tagline && (
          <div className="mb-10 flex justify-center">
            <span
              className="inline-flex rounded-full border px-4 py-2 text-[12px] font-normal uppercase leading-[1.2] tracking-[0.04em] md:text-[14px]"
              style={{ borderColor: GREEN, color: GREEN }}
            >
              {tagline}
            </span>
          </div>
        )}

        {isWithVideo
          ? (() => {
              const videoPrimary = slice.primary as WithVideoPrimary;
              const heroVideo = videoPrimary.hero_video;
              const heroPoster = videoPrimary.hero_poster;
              if (!isFilled.linkToMedia(heroVideo)) {
                return null;
              }
              return (
                <div className="relative mx-auto aspect-[16/9] w-full max-w-[960px] overflow-hidden rounded-[16px] bg-black">
                  <MotionSafeVideo
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    poster={
                      isFilled.image(heroPoster)
                        ? (heroPoster.url ?? undefined)
                        : undefined
                    }
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                  >
                    <source src={heroVideo.url} type="video/webm" />
                    <source src={heroVideo.url} type="video/mp4" />
                  </MotionSafeVideo>
                </div>
              );
            })()
          : isFilled.image((slice.primary as DefaultPrimary).hero_image) && (
              <div className="relative mx-auto aspect-[16/9] w-full max-w-[960px] overflow-hidden rounded-[16px]">
                <PrismicNextImage
                  field={(slice.primary as DefaultPrimary).hero_image}
                  fill
                  className="object-cover"
                  sizes="(min-width: 1024px) 960px, 100vw"
                  fallbackAlt=""
                />
              </div>
            )}

        {isFilled.richText(title) && (
          <div className="mx-auto mt-12 max-w-[820px] text-center [&_h2]:m-0 [&_h2]:text-[28px] md:[&_h2]:text-[36px] [&_h2]:font-semibold [&_h2]:leading-[1.2] [&_h2]:text-white">
            <PrismicRichText field={title} />
          </div>
        )}

        {isWithVideo
          ? (cards.length > 0 || footer_note) && (
              <div className="relative mt-14 overflow-hidden rounded-[24px] px-5 py-10 md:px-10 md:py-14">
                {isFilled.image(
                  (slice.primary as WithVideoPrimary).table_background,
                ) ? (
                  <div className="pointer-events-none absolute inset-0">
                    <PrismicNextImage
                      field={
                        (slice.primary as WithVideoPrimary).table_background
                      }
                      fill
                      className="object-cover opacity-60"
                      sizes="(min-width: 1024px) 1180px, 100vw"
                      fallbackAlt=""
                    />
                    <div className="absolute inset-0 bg-[#041118]/70" />
                  </div>
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-b from-[#0A1620] to-[#02080E]" />
                )}

                <div className="relative">
                  {cards.length > 0 &&
                    (!isMobile ? (
                      <div className="grid grid-cols-[140px_repeat(3,1fr)] gap-x-5 lg:grid-cols-[160px_repeat(3,1fr)] items-stretch">
                        <div className="flex flex-col">
                          <div className={`${HEADER_H}`} />
                          <div
                            className={`${ROW_H} flex items-start pt-6 border-b border-white/10`}
                          >
                            <p
                              className="m-0 text-[14px] font-semibold leading-[1.3]"
                              style={{ color: GREEN }}
                            >
                              {row_1_label}
                            </p>
                          </div>
                          <div
                            className={`${ROW_H} flex items-start pt-6 border-b border-white/10`}
                          >
                            <p
                              className="m-0 text-[14px] font-semibold leading-[1.3]"
                              style={{ color: GREEN }}
                            >
                              {row_2_label}
                            </p>
                          </div>
                        </div>

                        {cards.map((card, i) => (
                          <PhaseFullCard key={`card-${i}`} card={card} />
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-6">
                        {cards.map((card, i) => (
                          <div key={`m-${i}`} className="flex flex-col">
                            <PhaseFullCard card={card} />
                          </div>
                        ))}
                      </div>
                    ))}

                  {footer_note && (
                    <p
                      className="mt-12 text-center text-[15px] font-medium leading-[1.5] md:text-[16px]"
                      style={{ color: GREEN }}
                    >
                      {footer_note}
                    </p>
                  )}
                </div>
              </div>
            )
          : cards.length > 0 &&
            (!isMobile ? (
              <div className="mt-14 grid grid-cols-[160px_repeat(3,1fr)] gap-x-5 lg:grid-cols-[180px_repeat(3,1fr)]">
                <div />
                {cards.map((card, i) => (
                  <PhaseHeader key={`header-${i}`} card={card} />
                ))}

                <div className="flex min-h-[80px] items-start border-t border-white/10 pr-4 pt-5">
                  <p
                    className="m-0 text-[13px] font-semibold uppercase leading-[1.3] tracking-[0.04em]"
                    style={{ color: GREEN }}
                  >
                    {row_1_label}
                  </p>
                </div>
                {cards.map((card, i) => (
                  <div
                    key={`r1-${i}`}
                    className="min-h-[80px] border-t border-white/10 px-5 py-5"
                  >
                    {card.deliverables_text && (
                      <p className="m-0 text-[14px] font-normal leading-[1.5] text-white">
                        {card.deliverables_text}
                      </p>
                    )}
                  </div>
                ))}

                <div className="flex min-h-[80px] items-start border-t border-white/10 pr-4 pt-5">
                  <p
                    className="m-0 text-[13px] font-semibold uppercase leading-[1.3] tracking-[0.04em]"
                    style={{ color: GREEN }}
                  >
                    {row_2_label}
                  </p>
                </div>
                {cards.map((card, i) => (
                  <div
                    key={`r2-${i}`}
                    className="min-h-[80px] border-t border-white/10 px-5 py-5"
                  >
                    {card.capabilities_text && (
                      <p className="m-0 text-[14px] font-normal leading-[1.5] text-white">
                        {card.capabilities_text}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-12 flex flex-col gap-6">
                {cards.map((card, i) => (
                  <div key={`m-${i}`} className="flex flex-col">
                    <PhaseHeader card={card} />
                    <div className="border-t border-white/10 px-5 py-5">
                      {row_1_label && (
                        <p
                          className="m-0 mb-2 text-[12px] font-semibold uppercase tracking-[0.04em]"
                          style={{ color: GREEN }}
                        >
                          {row_1_label}
                        </p>
                      )}
                      {card.deliverables_text && (
                        <p className="m-0 text-[14px] font-normal leading-[1.5] text-white">
                          {card.deliverables_text}
                        </p>
                      )}
                    </div>
                    <div className="border-t border-white/10 px-5 py-5">
                      {row_2_label && (
                        <p
                          className="m-0 mb-2 text-[12px] font-semibold uppercase tracking-[0.04em]"
                          style={{ color: GREEN }}
                        >
                          {row_2_label}
                        </p>
                      )}
                      {card.capabilities_text && (
                        <p className="m-0 text-[14px] font-normal leading-[1.5] text-white">
                          {card.capabilities_text}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}

        {!isWithVideo && footer_note && (
          <p
            className="mt-14 text-center text-[15px] font-medium leading-[1.5] md:text-[16px]"
            style={{ color: GREEN }}
          >
            {footer_note}
          </p>
        )}
      </div>
    </section>
  );
};

export default VelxPhaseComparison;
